import { useCallback, useState } from 'react';
import { tripsApi, shouldQueueOffline } from '@/api/client';
import { queueEvent } from '@/offline/queue';
import { newCrewActionMeta } from '@/lib/offlineReplay';
import { useToast } from '@/components/Feedback';
import { CARGO_COPY, type CargoRequestBody } from '@/lib/crewCargoCheck';

export const CREW_QUEUED_MESSAGE = 'Saved on phone – will send when signal returns';

/**
 * Saves a stop's cargo check (no-manifest stops, CREW_API_CONTRACT §14.5) the same way on
 * the Trip and Scan screens: online first; with no signal the check is queued (`cargo_check`)
 * with the same clientEventId + device time as the tap, so a replay is never applied twice.
 */
export function useCrewCargoCheck(opts: {
  tripId: string | undefined;
  /** After the server accepted the check (reload the trip / next task). */
  onSaved?: () => Promise<unknown> | unknown;
  /** After the check was saved on the phone (refresh the queue overlay). */
  onQueued?: () => Promise<unknown> | unknown;
  /** After the server rejected it (e.g. 409 booking changed → reload so the mode updates). */
  onRejected?: () => Promise<unknown> | unknown;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const { tripId, onSaved, onQueued, onRejected } = opts;

  const submit = useCallback(
    async (stopId: string, body: CargoRequestBody) => {
      if (!tripId) return;
      setBusy(true);
      setError('');
      setMessage('');
      // One key + tap time for the online attempt and any offline replay of it.
      const action = newCrewActionMeta();
      try {
        const result = await tripsApi.confirmCargo(tripId, stopId, body, action);
        toast(result.cargoCheck?.mismatch ? CARGO_COPY.savedMismatch : CARGO_COPY.savedOk);
        await onSaved?.();
      } catch (err) {
        if (shouldQueueOffline(err)) {
          try {
            await queueEvent({
              type: 'cargo_check',
              payload: { tripId, stopId, ...body, deviceTimestamp: action.deviceTimestamp },
              ...action,
            });
            setMessage(CREW_QUEUED_MESSAGE);
            await onQueued?.();
          } catch (queueErr) {
            setError((queueErr as Error).message ?? 'Could not save the cargo check on this phone');
          }
        } else {
          setError((err as { message?: string }).message ?? 'Could not save the cargo check. Try again.');
          void onRejected?.();
        }
      } finally {
        setBusy(false);
      }
    },
    [tripId, toast, onSaved, onQueued, onRejected],
  );

  const clear = useCallback(() => {
    setMessage('');
    setError('');
  }, []);

  return { busy, message, error, submit, clear };
}
